import { jobTitleList } from "#src/common/helper/contactSupport/contactDetailFields";
import { useFormDropdown } from "#src/commonV2/form/Dropdown/useFormDropdown";
import { useFormInput } from "#src/commonV2/form/Input/useFormInput";
import patterns from "#src/config/patterns";
import { IWorkspacePaymentSettings } from "#src/services/payment/types";
import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ICustomerInfoRenderProps } from "./types";

export enum ECustomerInfoField {
  FULL_NAME = "fullName",
  PHONE_NUMBER = "phoneNumber",
  JOB_TITLE = "jobTitle",
  EMAIL = "email",
}

/** This hook is written to organize and split code into smaller chunks.
 * Reusability is not an intended feature, but if we need, and we can, that's good. */
export const useCustomerInfoForm = (
  defaultPayment: IWorkspacePaymentSettings | null
) => {
  const { t } = useTranslation("common", {
    keyPrefix: "v2.payment.enterInformation.customerInformation",
  });

  // --------------- INITIALIZATION ---------------

  const defaultJobTitle = useMemo(() => {
    const findJobTitle = jobTitleList.find(
      (item) => item.value === defaultPayment?.customer_job_title
    );
    if (findJobTitle) return findJobTitle;
    return jobTitleList[0];
  }, [defaultPayment]);

  // --------------- HOOKS ---------------

  const {
    value: fullName,
    reset: resetFullNameField,
    wasChanged: fullNameWasChanged,
    setExternalError: setFullNameExternalError,
    hasError: fullNameHasError,
    renderProps: fullNameFieldRenderProps,
  } = useFormInput({
    label: t("fullName.label"),
    placeholder: t("fullName.placeholder"),
    defaultValue: defaultPayment?.customer_full_name,
    validation: { required: true },
  });

  const {
    value: phoneNumber,
    reset: resetPhoneNumberField,
    wasChanged: phoneNumberWasChanged,
    setExternalError: setPhoneNumberExternalError,
    hasError: phoneNumberHasError,
    renderProps: phoneNumberFieldRenderProps,
  } = useFormInput({
    label: t("phoneNumber.label"),
    placeholder: t("phoneNumber.placeholder"),
    defaultValue: defaultPayment?.customer_phone_number,
    validation: { required: true, pattern: patterns.PHONE },
  });

  const {
    selected: jobTitle,
    reset: resetJobTitleField,
    wasChanged: jobTitleWasChanged,
    renderProps: jobTitleFieldRenderProps,
  } = useFormDropdown({
    id: "jobTitle",
    label: t("jobTitle.label"),
    defaultSelected: defaultJobTitle,
    options: jobTitleList,
    requiredMark: true,
  });

  const {
    value: email,
    reset: resetEmailField,
    wasChanged: emailWasChanged,
    setExternalError: setEmailExternalError,
    hasError: emailHasError,
    renderProps: emailFieldRenderProps,
  } = useFormInput({
    label: t("emailAddress.label"),
    placeholder: t("emailAddress.placeholder"),
    defaultValue: defaultPayment?.customer_email,
    validation: { required: true, pattern: patterns.EMAIL },
  });

  // --------------- METHODS ---------------

  const resetForm = useCallback(() => {
    resetFullNameField();
    resetPhoneNumberField();
    resetJobTitleField();
    resetEmailField();
  }, [
    resetFullNameField,
    resetPhoneNumberField,
    resetJobTitleField,
    resetEmailField,
  ]);

  // --------------- COMPUTED ---------------

  const value: { [F in ECustomerInfoField]: string } = useMemo(
    () => ({ fullName, phoneNumber, jobTitle: jobTitle.value ?? "", email }),
    [fullName, phoneNumber, jobTitle, email]
  );

  const renderProps: ICustomerInfoRenderProps = useMemo(
    () => ({
      fullName: fullNameFieldRenderProps,
      phoneNumber: phoneNumberFieldRenderProps,
      jobTitle: jobTitleFieldRenderProps,
      email: emailFieldRenderProps,
    }),
    [
      fullNameFieldRenderProps,
      phoneNumberFieldRenderProps,
      jobTitleFieldRenderProps,
      emailFieldRenderProps,
    ]
  );

  const setExternalError: {
    [F in Exclude<ECustomerInfoField, ECustomerInfoField.JOB_TITLE>]: Dispatch<
      SetStateAction<string>
    >;
  } = useMemo(
    () => ({
      fullName: setFullNameExternalError,
      phoneNumber: setPhoneNumberExternalError,
      email: setEmailExternalError,
    }),
    [
      setFullNameExternalError,
      setPhoneNumberExternalError,
      setEmailExternalError,
    ]
  );

  const hasError: boolean = useMemo(
    () => fullNameHasError || phoneNumberHasError || emailHasError,
    [fullNameHasError, phoneNumberHasError, emailHasError]
  );

  const wasChanged: boolean = useMemo(
    () =>
      fullNameWasChanged ||
      phoneNumberWasChanged ||
      jobTitleWasChanged ||
      emailWasChanged,
    [
      fullNameWasChanged,
      phoneNumberWasChanged,
      jobTitleWasChanged,
      emailWasChanged,
    ]
  );

  // --------------- RETURN ---------------

  return {
    value,
    setExternalError,
    hasError,
    resetForm,
    wasChanged,
    renderProps,
  };
};
